import { LoadingArea } from '@consigli/facade';
import {
  useBlobUpload,
  useGetPackagesByTypeQuery,
  useProjectId,
  useServiceName,
  useStartPipelineMutation,
  type ExtendedFile,
} from '@consigli/hooks';
import { DocumentPackageType, JobType, Language, PipelineName } from '@consigli/types';
import limit from 'p-limit';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { useCreateInsightPackage } from '@/features/package-creation/hooks/use-create-insight-package';
import { useValidateName } from '@/features/package-creation/hooks/use-validate-name';
import { useNavigationHistory } from '@/features/package-creation/use-navigation-history';
import { Nav } from '@/layouts/nav';
// The tender (primary_reference) and blank leveling template (bid_levelling)
// reference tags are owned by the Offer/Bid Review module; the wizard sets them
// at upload time. Bid files use their provider name as the tag instead.
import { BID_LEVELLING_REFERENCE, PRIMARY_REFERENCE } from '@/pages/offer/offer-references';
import { Route, ServiceIdIndexValue } from '@/routes';

import { BidDetailsStep } from './bid-details-step';
import { BidReviewStepper, type BidReviewStep } from './bid-review-stepper';
import { CompareStep } from './compare-step';
import {
  Bid,
  BID_REVIEW_SERVICES_DATA_KEY,
  BidReviewService,
  CompareConfig,
  createDefaultCompareConfig,
  createEmptyBid,
  GapsCompareMode,
} from './types';
import { useUploadProgress } from '../hooks/use-upload-progress';

export const BidReviewWizard = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const serviceName = useServiceName();
  const navigate = useNavigate();
  const { wizardBackButtonClick } = useNavigationHistory();

  const [activeIndex, setActiveIndex] = useState(0);
  const [packageName, setPackageName] = useState('');
  const [bids, setBids] = useState<Bid[]>([createEmptyBid()]);
  const [compareConfig, setCompareConfig] = useState<CompareConfig>(createDefaultCompareConfig());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [targetCounter, setTargetCounter] = useState(0);
  const [currentCounter, setCurrentCounter] = useState(0);

  const { isUploading, progress } = useUploadProgress(currentCounter, targetCounter);
  const { createDocumentPackage } = useCreateInsightPackage({
    setCurrentCounter,
    setTargetCounter,
  });
  const { upload } = useBlobUpload();
  const [startPipeline] = useStartPipelineMutation();

  const packageType = ServiceIdIndexValue[serviceName] as DocumentPackageType;

  // The Offer package is named after the package name the user enters, and
  // (name, project, type) is unique — a repeated name would fail creation. We
  // reuse the shared validator so the "name taken" check and message match the
  // other package-creation wizards. It returns a string when the name is taken.
  const validateName = useValidateName(useGetPackagesByTypeQuery, {
    projectId,
    type: packageType,
  });

  const steps: BidReviewStep[] = useMemo(
    () => [
      { id: 'bid-details', label: t('bid-review.steps.bid-details') },
      { id: 'compare', label: t('bid-review.steps.compare') },
    ],
    [t],
  );

  const completedManualBids = useMemo(
    () =>
      bids.filter(
        (bid) => bid.providerName.trim().length > 0 && bid.language !== '' && bid.files.length > 0,
      ),
    [bids],
  );

  const detailsValid = useMemo(() => {
    if (packageName.trim().length === 0) return false;
    const totalBids = completedManualBids.length + compareConfig.selectedOtherBids.length;
    if (totalBids === 0) return false;

    const tenderReady =
      compareConfig.gapsCompareMode === GapsCompareMode.EXISTING
        ? compareConfig.existingReferencePackageId !== ''
        : compareConfig.newReferenceFiles.length > 0;
    return tenderReady;
  }, [packageName, completedManualBids, compareConfig]);

  // An empty name disables Next (a required field, like the bid fields). A
  // duplicate name does not disable it — we let the user click and surface a
  // clear error instead of leaving them guessing at a dead button.
  const isDuplicatePackageName = useMemo(() => {
    const trimmed = packageName.trim();
    return trimmed.length > 0 && typeof validateName(trimmed) === 'string';
  }, [packageName, validateName]);

  const packageNameErrorKey = isDuplicatePackageName ? 'package-creation.package-name-taken' : null;

  const isSubmitEnabled = useMemo(() => {
    if (compareConfig.services.length === 0) return false;
    if (!detailsValid) return false;

    const riskSelected = compareConfig.services.includes(BidReviewService.RISK_INSIGHT_GAPS);
    const bidVsBidSelected = compareConfig.services.includes(BidReviewService.BID_VS_BID);

    // A tender is ready when an existing one is picked or a new one is uploaded.
    const tenderReady =
      compareConfig.gapsCompareMode === GapsCompareMode.EXISTING
        ? compareConfig.existingReferencePackageId !== ''
        : compareConfig.newReferenceFiles.length > 0;
    // The leveling sheet is ready only once its file is actually uploaded.
    const levelingReady =
      compareConfig.autoFillLevelingSheet && compareConfig.levelingSheetFiles.length > 0;

    // Bid gaps always compares against a tender (existing or newly uploaded).
    if (riskSelected && !tenderReady) return false;

    if (bidVsBidSelected) {
      if (riskSelected) {
        // Both: the tender (required above) is the baseline. The leveling sheet
        // is optional, but if the user opted in they must upload its file.
        if (compareConfig.autoFillLevelingSheet && !levelingReady) return false;
      } else {
        // Bid vs Bid only: a leveling sheet (auto-fill on + file uploaded) or a
        // tender provides the comparison baseline.
        if (compareConfig.autoFillLevelingSheet ? !levelingReady : !tenderReady) return false;
      }
    }

    return true;
  }, [compareConfig, detailsValid]);

  const handleAddBid = useCallback(() => {
    setBids((current) => [...current, createEmptyBid()]);
  }, []);

  const handleDetailsNext = useCallback(() => {
    if (isDuplicatePackageName) {
      toast.error(t('bid-review.duplicate-package-name', { name: packageName.trim() }));
      return;
    }
    setActiveIndex(1);
  }, [isDuplicatePackageName, packageName, t]);

  const handleBack = useCallback(() => {
    if (activeIndex === 0) {
      wizardBackButtonClick();
      return;
    }
    setActiveIndex((index) => index - 1);
  }, [activeIndex, wizardBackButtonClick]);

  const handleSubmit = useCallback(async () => {
    if (!isSubmitEnabled) return;

    // The Offer package is named after the package name the user entered. Bail
    // out early with a clear message if a package with that name already exists,
    // instead of letting core-api reject the duplicate with a raw integrity error.
    const trimmedPackageName = packageName.trim();
    if (typeof validateName(trimmedPackageName) === 'string') {
      toast.error(t('bid-review.duplicate-package-name', { name: trimmedPackageName }));
      return;
    }

    setIsSubmitting(true);
    try {
      const riskSelected = compareConfig.services.includes(BidReviewService.RISK_INSIGHT_GAPS);
      const bidVsBidSelected = compareConfig.services.includes(BidReviewService.BID_VS_BID);
      // The tender / RFP is selected in the Bid Details step (always visible).
      // When Bid vs. Bid auto-fills a leveling sheet, the sheet replaces the
      // tender as the comparison baseline, so the tender selection is ignored.
      const gapsPackageActive =
        riskSelected || (bidVsBidSelected && !compareConfig.autoFillLevelingSheet);
      const uploadNewTender =
        gapsPackageActive && compareConfig.gapsCompareMode === GapsCompareMode.UPLOAD_NEW;

      const uploadLevelingSheet =
        bidVsBidSelected &&
        compareConfig.autoFillLevelingSheet &&
        compareConfig.levelingSheetFiles.length > 0;

      const bidsToSubmit = completedManualBids;
      const manualBidFileCount = bidsToSubmit.reduce((sum, bid) => sum + bid.files.length, 0);
      const totalFiles =
        manualBidFileCount +
        (uploadNewTender ? compareConfig.newReferenceFiles.length : 0) +
        (uploadLevelingSheet ? compareConfig.levelingSheetFiles.length : 0);
      setCurrentCounter(0);
      setTargetCounter(totalFiles);

      const queue = limit(100);
      const uploadWithCount = (file: File, packageId: string, metadata?: Record<string, unknown>) =>
        queue(async () => {
          await upload(file as ExtendedFile, packageId, undefined, metadata);
          setCurrentCounter((counter) => counter + 1);
        });
      const primaryLanguage =
        bidsToSubmit.length > 0 && bidsToSubmit[0].language
          ? (bidsToSubmit[0].language as Language)
          : Language.EN;
      const bidslist = bidsToSubmit.map((bid) => bid.providerName.trim());

      // The existing tender / RFP the bids are compared against for gaps. It
      // stays its own package; a newly uploaded one has no separate package —
      // its files live in this same Offer package tagged as the primary
      // reference — so existingPackageId is empty then.
      const existingPackageId =
        gapsPackageActive && !uploadNewTender ? compareConfig.existingReferencePackageId : '';

      // Bids pulled in from other Offer packages on the project, persisted so
      // the pipeline can include them in the comparison.
      const otherBids = compareConfig.selectedOtherBids.map((bid) => ({
        packageId: bid.packageId,
        providerName: bid.providerName,
      }));

      // All bids (and any uploaded tender / RFP) live in a single Offer
      // package, named after the package name the user entered. The existing
      // package id, bid list, other bids, and the selected services are
      // persisted in `data`: breeze reads them to branch the pipeline and the
      // UI reads `services` to decide which tabs to show.
      const packageId = await createDocumentPackage(
        packageType,
        trimmedPackageName,
        primaryLanguage,
        undefined,
        compareConfig.selectedProcessors,
        undefined,
        {
          existingPackageId,
          bidslist,
          otherBids,
          [BID_REVIEW_SERVICES_DATA_KEY]: compareConfig.services,
        },
      );
      if (!packageId) {
        throw new Error('Failed to create bid review package');
      }

      // Every blob carries a `reference` tag identifying its source: the
      // provider name for a bid file, or `primary_reference` for a file from
      // a newly uploaded tender / RFP.
      const bidUploads = bidsToSubmit.flatMap((bid) =>
        bid.files.map((file) =>
          uploadWithCount(file, packageId, { reference: bid.providerName.trim() }),
        ),
      );
      const tenderUploads = uploadNewTender
        ? compareConfig.newReferenceFiles.map((file) =>
            uploadWithCount(file, packageId, { reference: PRIMARY_REFERENCE }),
          )
        : [];
      // The blank leveling / assessment template, tagged so the pipeline can
      // find and auto-fill it from the bids.
      const levelingUploads = uploadLevelingSheet
        ? compareConfig.levelingSheetFiles.map((file) =>
            uploadWithCount(file, packageId, { reference: BID_LEVELLING_REFERENCE }),
          )
        : [];
      await Promise.allSettled([...bidUploads, ...tenderUploads, ...levelingUploads]);

      // The bid review pipeline's mirroring stage reads `existing_package_id`
      // from the package `data` and copies that tender / RFP's files into this
      // Offer package tagged as the primary reference — no cross-package compare.
      await startPipeline({
        projectId,
        packageId,
        pipelineName: PipelineName.BID_REVIEW,
        notify: true,
        jobType: JobType.DOCUMENT_PACKAGE,
      }).unwrap();

      toast.success(t('package-creation.success-analyzed'));
      navigate(
        `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${packageId}/${Route.DASHBOARD}`,
      );
    } catch (error) {
      console.error('Error creating bid review:', error);
      toast.error(t('package-creation.package-create-failed'));
    } finally {
      setIsSubmitting(false);
      setCurrentCounter(0);
      setTargetCounter(0);
    }
  }, [
    isSubmitEnabled,
    compareConfig,
    completedManualBids,
    packageName,
    validateName,
    createDocumentPackage,
    packageType,
    upload,
    startPipeline,
    projectId,
    serviceName,
    navigate,
    t,
  ]);

  const subtitle = activeIndex === 1 ? t('bid-review.compare.subtitle') : '';

  return (
    <div className="w-full flex flex-col min-h-[calc(100vh-theme(space.14))] bg-day-light-4">
      <Nav projectId={projectId} handleClick={wizardBackButtonClick} />
      <div className="flex flex-col flex-1 items-center px-4 py-12 w-full sm:w-[45rem] mx-auto">
        <div className="font-semibold text-2xl text-day-neutral-dark">
          {t(`package-creation.new.${serviceName}`)}
        </div>
        {subtitle && <p className="mt-2 text-center text-sm text-day-neutral-subtle">{subtitle}</p>}
        <div className="mb-10 mt-8 w-full max-w-xl">
          <BidReviewStepper steps={steps} activeIndex={activeIndex} />
        </div>

        {isSubmitting || isUploading ? (
          <LoadingArea
            className="mt-20"
            title={t('document-upload.uploading')}
            message={progress < 1 ? t('document-upload.initialising') : `${progress}%`}
          />
        ) : activeIndex === 0 ? (
          <BidDetailsStep
            packageName={packageName}
            onPackageNameChange={setPackageName}
            nameErrorKey={packageNameErrorKey}
            bids={bids}
            onBidsChange={setBids}
            compareConfig={compareConfig}
            onCompareConfigChange={setCompareConfig}
            selectedOtherBids={compareConfig.selectedOtherBids}
            onSelectedOtherBidsChange={(selectedOtherBids) =>
              setCompareConfig((current) => ({ ...current, selectedOtherBids }))
            }
            onAddBid={handleAddBid}
            onBack={handleBack}
            onNext={handleDetailsNext}
            isNextEnabled={detailsValid}
          />
        ) : (
          <CompareStep
            config={compareConfig}
            onConfigChange={setCompareConfig}
            onBack={handleBack}
            onSubmit={handleSubmit}
            isSubmitEnabled={isSubmitEnabled}
          />
        )}
      </div>
    </div>
  );
};
